import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Search, Filter, Users, Building2, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useRoleOptions,
  usePropertyOptions,
} from '@/hooks/queries/useDropdowns';

interface UserFiltersProps {
  searchTerm: string;
  selectedRole: string;
  selectedProperty: string;
  onSearchChange: (value: string) => void;
  onRoleChange: (value: string) => void;
  onPropertyChange: (value: string) => void;
  onReset?: () => void;
  className?: string;
}

export function UserFilters({
  searchTerm,
  selectedRole,
  selectedProperty,
  onSearchChange,
  onRoleChange,
  onPropertyChange,
  onReset,
  className,
}: UserFiltersProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Use API hooks for dropdown data
  const { options: roleOptions } = useRoleOptions();
  const { options: propertyOptions, isLoading: propertiesLoading } =
    usePropertyOptions();

  // Check if any filters are active
  const hasActiveFilters =
    searchTerm || selectedRole !== 'all' || selectedProperty !== 'all';

  const handleReset = () => {
    onSearchChange('');
    onRoleChange('all');
    onPropertyChange('all');
    onReset?.();
  };

  return (
    <Card className={cn('mb-6', className)}>
      <CardContent className="p-4 sm:p-6">
        <div
          className={cn(
            'flex items-center justify-between mb-4',
            isRTL ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          <div
            className={cn(
              'flex items-center gap-2',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Filter className="w-4 h-4 text-muted-foreground" />
            <h3
              className={cn(
                'text-sm font-medium',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t('users.filters.title', 'Filters')}
            </h3>
          </div>

          {/* Reset Button - only show when filters are active */}
          {hasActiveFilters && onReset && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className={cn(
                'text-xs h-7 px-2',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <X className={cn('w-3 h-3', isRTL ? 'ml-1' : 'mr-1')} />
              {t('users.filters.reset', 'Reset')}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search Input */}
          <div className="space-y-2">
            <Label
              htmlFor="search"
              className={cn(
                'text-sm font-medium',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t('users.filters.searchByName')}
            </Label>
            <div className="relative">
              <Search
                className={cn(
                  'absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground',
                  isRTL ? 'right-3' : 'left-3'
                )}
              />
              <Input
                id="search"
                value={searchTerm}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={t('users.filters.placeholders.searchUsers')}
                className={cn(isRTL ? 'pr-10 text-right' : 'pl-10 text-left')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>
          </div>

          {/* Role Filter */}
          <div className="space-y-2">
            <Label
              htmlFor="role"
              className={cn(
                'text-sm font-medium',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t('users.filters.filterByRole')}
            </Label>
            <Select value={selectedRole} onValueChange={onRoleChange}>
              <SelectTrigger
                className={cn(isRTL ? 'text-right' : 'text-left')}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                <SelectValue
                  placeholder={t('users.filters.placeholders.allRoles')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div
                    className={cn(
                      'flex items-center gap-2',
                      isRTL ? 'flex-row-reverse' : 'flex-row'
                    )}
                  >
                    <Users className="w-4 h-4" />
                    {t('users.filters.allRoles')}
                  </div>
                </SelectItem>
                {roleOptions.map(role => (
                  <SelectItem key={role.value} value={role.value}>
                    <div
                      className={cn(
                        'flex items-center gap-2',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <Users className="w-4 h-4" />
                      {role.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Property Filter */}
          <div className="space-y-2">
            <Label
              htmlFor="property"
              className={cn(
                'text-sm font-medium',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t('users.filters.filterByProperty')}
            </Label>
            <Select value={selectedProperty} onValueChange={onPropertyChange}>
              <SelectTrigger
                className={cn(isRTL ? 'text-right' : 'text-left')}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                <SelectValue
                  placeholder={t('users.filters.placeholders.allProperties')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div
                    className={cn(
                      'flex items-center gap-2',
                      isRTL ? 'flex-row-reverse' : 'flex-row'
                    )}
                  >
                    <Building2 className="w-4 h-4" />
                    {t('users.filters.allProperties', 'All Properties')}
                  </div>
                </SelectItem>
                {propertiesLoading ? (
                  <SelectItem value="loading" disabled>
                    <div
                      className={cn(
                        'flex items-center gap-2',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t(
                        'users.filters.loadingProperties',
                        'Loading properties...'
                      )}
                    </div>
                  </SelectItem>
                ) : (
                  propertyOptions
                    .filter(option => option.value !== '') // Skip the default "All Properties" option from API
                    .map(property => (
                      <SelectItem key={property.value} value={property.value}>
                        <div
                          className={cn(
                            'flex items-center gap-2',
                            isRTL ? 'flex-row-reverse' : 'flex-row'
                          )}
                        >
                          <Building2 className="w-4 h-4" />
                          <span className="font-medium">{property.label}</span>
                        </div>
                      </SelectItem>
                    ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
